import { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "@provenleap/ui";
import {
  MagnifyingGlass,
  Check,
  CaretDown,
  Cpu,
  Heartbeat,
  Bank,
  ShoppingCart,
  GraduationCap,
  Buildings,
  Scales,
  Factory,
  ForkKnife,
  Car,
  HandHeart,
  DotsThree,
  Storefront,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import type { Icon } from "@phosphor-icons/react";

export const INDUSTRY_OPTIONS: { label: string; icon: Icon }[] = [
  { label: "Technology / SaaS", icon: Cpu },
  { label: "Health & Wellness", icon: Heartbeat },
  { label: "Finance & Banking", icon: Bank },
  { label: "E-commerce", icon: ShoppingCart },
  { label: "Education", icon: GraduationCap },
  { label: "Real Estate", icon: Buildings },
  { label: "Legal", icon: Scales },
  { label: "Local Services", icon: Storefront },
  { label: "Manufacturing", icon: Factory },
  { label: "Restaurant & Hospitality", icon: ForkKnife },
  { label: "Automotive", icon: Car },
  { label: "Non-Profit", icon: HandHeart },
  { label: "Other", icon: DotsThree },
];

const INDUSTRY_KEYWORDS: Record<string, Icon> = {
  "tech": Cpu,
  "saas": Cpu,
  "software": Cpu,
  "health": Heartbeat,
  "wellness": Heartbeat,
  "medical": Heartbeat,
  "finance": Bank,
  "banking": Bank,
  "retail": ShoppingCart,
  "commerce": ShoppingCart,
  "shopping": ShoppingCart,
  "educat": GraduationCap,
  "real estate": Buildings,
  "property": Buildings,
  "legal": Scales,
  "law": Scales,
  "manufactur": Factory,
  "restaurant": ForkKnife,
  "hospitality": ForkKnife,
  "food": ForkKnife,
  "auto": Car,
  "vehicle": Car,
  "non-profit": HandHeart,
  "nonprofit": HandHeart,
  "charity": HandHeart,
  "fashion": ShoppingCart,
  "apparel": ShoppingCart,
  "local service": Storefront,
  "consulting": Scales,
  "agency": Buildings,
};

export function getIndustryIcon(label: string): Icon | undefined {
  // Exact match first
  const exact = INDUSTRY_OPTIONS.find((o) => o.label === label);
  if (exact) return exact.icon;
  // Keyword match
  const lower = label.toLowerCase();
  for (const [keyword, icon] of Object.entries(INDUSTRY_KEYWORDS)) {
    if (lower.includes(keyword)) return icon;
  }
  return undefined;
}

interface IndustryPickerProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}

export function IndustryPicker({ value, onChange, id }: IndustryPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const filtered = useMemo(
    () => INDUSTRY_OPTIONS.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
    [search]
  );

  const selectedOption = INDUSTRY_OPTIONS.find((o) => o.label === value);

  function handleSelect(option: string) {
    onChange(option === value ? "" : option);
    setIsOpen(false);
    setSearch("");
  }

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-11 w-full items-center justify-between rounded-lg border-[0.5px] border-input bg-background px-3 py-2 text-sm shadow-xs cursor-pointer"
      >
        <span className={`flex items-center gap-2 ${value ? "text-foreground" : "text-muted-foreground"}`}>
          {selectedOption && <selectedOption.icon size={16} className="shrink-0 text-foreground" />}
          {value || "Select an industry"}
        </span>
        <CaretDown size={16} className="opacity-50" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 4 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute left-0 bottom-full mb-2.5 z-[60] w-full rounded-xl bg-popover shadow-md"
            style={{ border: "0.5px solid var(--hairline)" }}
          >
            {/* Arrow pointing down */}
            <div
              className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-popover rotate-45 z-10"
              style={{ borderRight: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" }}
            />
            <div className="px-2 pt-2 pb-0.5">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search industries..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7 h-10 text-sm border-[0.5px] border-input shadow-none focus-visible:ring-0 focus-visible:outline-none focus:outline-none rounded-lg"
                />
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto p-2">
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">
                  No industries found
                </p>
              )}
              {filtered.map((option) => {
                const OptionIcon = option.icon;
                return (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleSelect(option.label)}
                    className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                  >
                    <OptionIcon size={16} className="shrink-0 text-foreground" />
                    <span className="flex-1 text-left">{option.label}</span>
                    {value === option.label && (
                      <Check size={14} className="text-primary shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
